import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { 
  Video, Shield, UserCheck, CheckCircle2, 
  ArrowRight, FileCheck, Target, Sparkles,
  BadgeCheck, AlertTriangle, Eye, Zap,
  Users, Clock, BarChart3, Crown, XCircle,
  Lightbulb, FileText, TrendingUp, Building2, Menu, X, Inbox
} from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

export default function About() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "About Vexis | Built for Recruitment Agencies";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
            <span className="text-white font-bold tracking-widest text-sm">VEXIS</span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <Link href="/about" className="text-sm font-medium text-white transition-colors">About</Link>
            <Link href="/contact" className="text-sm font-medium transition-colors" style={{ color: "#94a3b8" }}
              onMouseEnter={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseLeave={e => (e.currentTarget.style.color = "#94a3b8")}>Contact</Link>
          </div>
          <div className="hidden lg:flex gap-3">
            <Link href="/login">
              <Button variant="ghost" className="font-medium text-slate-300 hover:text-white hover:bg-white/10" data-testid="button-portal-access">
                Sign In
              </Button>
            </Link>
            <Link href="/contact">
              <Button className="font-semibold text-white" style={{ backgroundColor: "#6366f1" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#6366f1")}
                data-testid="button-request-demo">
                Submit Enquiry
              </Button>
            </Link>
          </div>
          <button
            className="lg:hidden p-2"
            style={{ color: "#ffffff" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }}>
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <Link href="/about" className="text-sm font-medium text-white py-2" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link href="/contact" className="text-sm font-medium py-2" style={{ color: "#94a3b8" }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <div className="flex flex-col gap-2 pt-4" style={{ borderTop: "1px solid rgba(99,102,241,0.15)" }}>
                <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full font-medium text-slate-300 hover:text-white hover:bg-white/10">
                    Sign In
                  </Button>
                </Link>
                <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full font-semibold text-white" style={{ backgroundColor: "#6366f1" }}>
                    Submit Enquiry
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      <section className="relative overflow-hidden bg-slate-900">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/vexis-bg-hero.jpg')" }}
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/50 via-slate-900/60 to-slate-950/80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
        
        <div className="container mx-auto px-4 py-14 md:py-28 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-8">
              <Crown className="w-4 h-4" />
              <span>Recruitment Assessment Platform</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-white mb-6">
              Your Recruitment Stack.{" "}
              <span className="bg-gradient-to-r from-blue-400 via-primary to-blue-400 bg-clip-text text-transparent">
                Your Assessment Standard.
              </span>
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Vexis connects to your recruitment platform and adds structured candidate assessment — without changing how you work.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 md:p-12 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.18)" }}>
              <div className="space-y-8">
                <p className="text-xl leading-relaxed" style={{ color: "#cbd5e1" }}>
                  Vexis is a recruitment assessment SaaS that sits inside your existing workflow. Connect your ATS via OAuth, send candidates a token link, and receive a reviewed, boardroom-ready dossier written directly back to their record — without switching platforms, adding headcount, or changing how your team operates.
                </p>

                <div className="border-l-4 pl-6 py-2" style={{ borderColor: "#6366f1" }}>
                  <p className="text-lg leading-relaxed" style={{ color: "#cbd5e1" }}>
                    Candidates complete a structured video assessment in under 15 minutes on any device. No app, no account, no friction. Every submission is reviewed by a human before any outcome is set. Your team sees only finalised, decision-ready profiles.
                  </p>
                </div>

                <p className="text-lg leading-relaxed" style={{ color: "#94a3b8" }}>
                  Vexis works alongside the tools you already use. Recruiters stay in their platform. Candidates get a seamless experience from their own inbox. And every dossier arrives formatted for the people making the decision — not just the people running the process.
                </p>

                <div className="rounded-xl p-6" style={{ backgroundColor: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)" }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(34,197,94,0.15)" }}>
                      <Shield className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2 text-lg">Human Review. Every Time.</h3>
                      <p style={{ color: "#94a3b8" }}>
                        No candidate reaches an outcome without a completed agent review. AI handles transcription and scoring. A human reviews before anything is sent. Your clients receive a shortlist they can trust — not a score sheet they have to explain.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                The Challenges We Help Address
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                What recruiters run into when assessment doesn't scale with volume.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">High Volume, Low Signal</h3>
                <p className="text-slate-400 text-sm">Too many applications, not enough insight. Volume without structure makes early-stage evaluation inconsistent and slow.</p>
              </div>
              
              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Assessment Bottlenecks</h3>
                <p className="text-slate-400 text-sm">Manual screening creates delays that cost you candidates. The best applicants move on before you've had a chance to evaluate them.</p>
              </div>
              
              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Inconsistent Standards</h3>
                <p className="text-slate-400 text-sm">Without a structured process, every recruiter assesses differently. Inconsistency at the early stage creates risk later.</p>
              </div>
              
              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Platform Switching</h3>
                <p className="text-slate-400 text-sm">Jumping between tools to manage candidates breaks workflow and creates data gaps. Assessment should live where your recruitment does.</p>
              </div>
              
              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-slate-500/20 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Administrative Overhead</h3>
                <p className="text-slate-400 text-sm">Coordination, follow-up, and formatting take time away from the work that actually moves candidates forward.</p>
              </div>

              <div className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Slow Time-to-Shortlist</h3>
                <p className="text-slate-400 text-sm">When candidates aren't assessed promptly, they're already off the market. Delays between application and first evaluation cost you the best people.</p>
              </div>
            </div>

            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                The Benefits of Using Vexis
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                What changes when assessment is built into your workflow.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl border border-primary/30">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Earlier Candidate Insight</h3>
                <p className="text-slate-300 text-sm">Structured video assessment gives you real signal at the application stage — before you've committed time to an unstructured first-round video assessment.</p>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-xl border border-green-500/30">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Faster, Consistent Output</h3>
                <p className="text-slate-300 text-sm">Every candidate goes through the same process. Every dossier arrives in the same format. Your team makes faster decisions with less effort.</p>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-xl border border-blue-500/30">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Reduced Review Load</h3>
                <p className="text-slate-300 text-sm">AI triage surfaces what matters. Your reviewers spend their time on the candidates worth it — not on filtering noise.</p>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-xl border border-purple-500/30">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <FileCheck className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Client-Ready Summaries</h3>
                <p className="text-slate-300 text-sm">Dossiers are formatted for the people making the hiring decision, not just the people running the process. Share with confidence.</p>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 rounded-xl border border-cyan-500/30">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">A Process You Can Stand Behind</h3>
                <p className="text-slate-300 text-sm">Structured, documented, human-reviewed. Every assessment follows the same standard. Every outcome is defensible.</p>
              </div>

              <div className="p-6 bg-gradient-to-br from-amber-500/20 to-amber-500/5 rounded-xl border border-amber-500/30">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Full Pipeline Visibility</h3>
                <p className="text-slate-300 text-sm">Track every candidate from application to outcome in one place. No chasing updates, no status calls — your team always knows exactly where things stand.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 md:p-12 rounded-2xl" style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)" }}>
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-medium border border-white/20">
                  <Building2 className="w-4 h-4" />
                  <span>Subscription Plans</span>
                </div>
                
                <h2 className="text-2xl md:text-3xl font-bold">
                  Built for Teams at Every Stage
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
                  Vexis is available on Free, Starter, Professional, and Enterprise plans. Every plan includes your own sending domain, the full assessment pipeline, and access to the Vexis template library. Higher tiers unlock template customisation, increased candidate volume, and white label options for agencies running Vexis under their own brand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/vexis-bg-hero.jpg')" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(79,70,229,0.82) 0%, rgba(30,27,75,0.78) 50%, rgba(37,99,235,0.80) 100%)" }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Your recruitment platform.<br />Your assessment standard. Your shortlist.
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact">
                <Button size="lg" className="text-lg px-8 bg-white text-slate-900 hover:bg-slate-100 h-14" data-testid="button-schedule-consultation">
                  Make an Enquiry
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link href="/contact">
                <Button size="lg" variant="outline" className="text-lg px-8 h-14 border-white/30 text-white hover:bg-white/10" data-testid="button-view-pricing">
                  Talk to Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="text-white py-12 md:py-16" style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 md:gap-8">
            <div>
              <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-slate-400">
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
              <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link href="/services" className="hover:text-white transition-colors">Services</Link>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-slate-500">
                &copy; 2026 Vexis Talent Agency
              </p>
              <p className="text-xs text-slate-600 mt-2">
                Vexis Talent Agency is a trading name of LHA Holdings Ltd.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
